import { useEffect, useState, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { listCategories, createRequest, me } from '@/api';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ImagePlus, MapPin, X, Camera, CheckCircle2, ArrowLeft, Send } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';

const LOGO_URL = '/logo.png';

const DISTRETTI_TICINO = [
  "Bellinzona",
  "Blenio",
  "Leventina",
  "Locarno",
  "Lugano",
  "Mendrisio",
  "Riviera",
  "Vallemaggia"
];

// NPA (Swiss postal codes) to district mapping for Canton Ticino (6500-6999)
function npaTodistretto(npa) {
  const n = parseInt(npa, 10);
  if (isNaN(n) || n < 6500 || n > 6999) return null;
  
  // Mendrisio district (6800-6899)
  if (n >= 6800 && n <= 6899) return "Mendrisio";
  // Lugano district (6900-6999, 6500-6599)
  if ((n >= 6900 && n <= 6999) || (n >= 6500 && n <= 6599)) return "Lugano";
  // Locarno district (6600-6699)
  if (n >= 6600 && n <= 6699) return "Locarno";
  // Bellinzona district (6500-6549 some areas)
  if (n >= 6500 && n <= 6528) return "Bellinzona";
  // Leventina (6700-6799 northern valleys)
  if (n >= 6760 && n <= 6799) return "Leventina";
  // Blenio (6710-6749)
  if (n >= 6710 && n <= 6749) return "Blenio";
  // Riviera (6700-6719)
  if (n >= 6700 && n <= 6719) return "Riviera";
  // Vallemaggia (6670-6699)
  if (n >= 6670 && n <= 6699) return "Vallemaggia";
  
  return null;
}

export default function NewRequest(){
  const { t } = useLanguage();
  const [cats,setCats]=useState([]);
  const [categoryId,setCategoryId]=useState('');
  const [title,setTitle]=useState('');
  const [description,setDescription]=useState('');
  const [urgent,setUrgent]=useState(false);
  const [cap,setCap]=useState('');
  const [street,setStreet]=useState('');
  const fileInputRef = useRef(null);
  const [number,setNumber]=useState('');
  const [city,setCity]=useState('');
  const [days,setDays]=useState([]);
  const [slots,setSlots]=useState([]);
  const [photos,setPhotos]=useState([]);
  const [successOpen, setSuccessOpen] = useState(false);
  const [roleChecked, setRoleChecked] = useState(false);
  const [contactPreference, setContactPreference] = useState('whatsapp');
  const [contactValue, setContactValue] = useState('');
  const handleCapChange = (val) => {
    setCap(val);
    if (val.length === 4) {
      const dist = npaTodistretto(val);
      if (dist) setCity(dist);
    }
  };
  const cameraInputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const checkRole = async () => {
      try {
        const res = await me();
        if (res.data.role !== 'client') { toast.error('Solo i clienti possono creare richieste'); nav('/'); return; }
        setRoleChecked(true);
      } catch (err) { toast.error('Sessione scaduta, effettua il login'); nav('/login'); }
    };
    checkRole();
  }, [nav]);

  const DAYS = [t('days.monday'),t('days.tuesday'),t('days.wednesday'),t('days.thursday'),t('days.friday'),t('days.saturday'),t('days.sunday')];
  const SLOTS = [{ key:'morning', label: t('timeSlots.morning') },{ key:'afternoon', label: t('timeSlots.afternoon') },{ key:'evening', label: t('timeSlots.evening') },{ key:'any', label: t('timeSlots.any') }];

  useEffect(()=>{ if (roleChecked) listCategories().then(r=>{ const data=r.data||[]; setCats(data); if(data.length) setCategoryId(data[0].id); }); },[roleChecked]);

  if (!roleChecked) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="text-slate-500">Caricamento...</div></div>;

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (photos.length + files.length > 5) { toast.error(t('newRequest.maxPhotos')); return; }
    const newPhotos = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 5 * 1024 * 1024) continue;
      try { const base64 = await new Promise((resolve, reject) => { const reader = new FileReader(); reader.onloadend = () => resolve(reader.result); reader.onerror = reject; reader.readAsDataURL(file); }); newPhotos.push(base64); } catch (err) {}
    }
    setPhotos(prev => [...prev, ...newPhotos]);
    if (e.target) e.target.value = '';
  };

  const removePhoto = (index) => setPhotos(prev => prev.filter((_, i) => i !== index));
  const toggleDay=(d)=> setDays(prev=> prev.includes(d)? prev.filter(x=>x!==d): [...prev,d]);
  const toggleSlot=(k)=> setSlots(prev=> prev.includes(k)? prev.filter(x=>x!==k): [...prev,k]);

  const onSubmit=async(e)=>{
    e.preventDefault();
    if (!categoryId) { toast.error(t('newRequest.selectCategoryError')); return; }
    if (!title.trim()) { toast.error(t('newRequest.titleError')); return; }
    if (!description.trim()) { toast.error(t('newRequest.descriptionError')); return; }
    if (!cap.trim()) { toast.error(t('newRequest.capError')); return; }
    if (!street.trim()) { toast.error(t('newRequest.streetError')); return; }
    if (!number.trim()) { toast.error(t('newRequest.numberError')); return; }
    if (!city) { toast.error(t('newRequest.districtError')); return; }
    if (days.length === 0) { toast.error(t('newRequest.daysError')); return; }
    if (slots.length === 0) { toast.error(t('newRequest.slotsError')); return; }
    if (!contactValue.trim()) { toast.error(t('ui.enterContact')); return; }
    try{
      await createRequest({ categoryId, title: title.trim(), description: description.trim(), urgent, photos, address:{ cap: cap.trim(), street: street.trim(), number: number.trim(), city, countryCode: 'CH' }, preferredDays: days, preferredSlots: slots, contactPreference, contactValue: contactValue.trim() });
      setSuccessOpen(true);
    }catch(err){
      if (err?.response?.status === 429) toast.error('Hai raggiunto il limite giornaliero di richieste. Riprova domani.');
      else if (err?.response?.data?.detail) toast.error(err.response.data.detail);
      else toast.error(t('common.error'));
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button onClick={() => nav('/cliente/dashboard')} className="p-2 hover:bg-slate-100 rounded-lg"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
              <Link to="/"><img src={LOGO_URL} alt="ServiceGo" className="h-10 w-auto" /></Link>
            </div>
            <LanguageSelector />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="new-request-page">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{t('newRequest.title')}</h1>
          <p className="text-slate-500 mt-1">{t('ui.describeYourNeed')}</p>
        </div>

        <form onSubmit={onSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {/* Category & Title */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('newRequest.selectCategory')}</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Categoria <span className="text-rose-500">*</span></label>
                    <Select value={categoryId} onValueChange={setCategoryId}>
                      <SelectTrigger data-testid="request-category" className="h-11 rounded-xl"><SelectValue placeholder={t('common.search')} /></SelectTrigger>
                      <SelectContent className="bg-white rounded-xl max-h-60">
                        {cats.map(c => { const key = `categories.${c.name}`; const translated = t(key); const displayName = (translated === key || translated.startsWith('categories.')) ? c.name : translated; return <SelectItem key={c.id} value={c.id}>{displayName}</SelectItem>; })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">{t('newRequest.requestTitle')} <span className="text-rose-500">*</span></label>
                    <Input data-testid="request-title" placeholder="Es. Riparazione perdita rubinetto" value={title} onChange={e=>setTitle(e.target.value)} className="h-11 rounded-xl" />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">{t('newRequest.description')} <span className="text-rose-500">*</span></label>
                  <Textarea data-testid="request-description" placeholder={t('newRequest.descriptionPlaceholder')} value={description} onChange={e=>setDescription(e.target.value)} className="min-h-[120px] rounded-xl" />
                </div>
                <div className="mt-4 flex items-center gap-3" data-testid="urgent-row">
                  <Checkbox data-testid="urgent-checkbox" checked={urgent} onCheckedChange={(v)=>setUrgent(Boolean(v))} className="h-5 w-5" />
                  <span className="text-sm text-slate-700">{t('newRequest.urgent')}</span>
                </div>
              </div>

              {/* Photos */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-2">{t('newRequest.attachPhotos')}</h2>
                <p className="text-xs text-slate-500 mb-4">{t('newRequest.maxPhotos')} ({photos.length}/5)</p>
                {photos.length > 0 && (
                  <div className="grid grid-cols-5 gap-2 mb-4">
                    {photos.map((photo, idx) => (
                      <div key={idx} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100">
                        <img src={photo} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removePhoto(idx)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-rose-500 text-white flex items-center justify-center shadow"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
                {photos.length < 5 && (
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button" onClick={() => fileInputRef.current?.click()} data-testid="upload-photo" className="bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 p-4 flex flex-col items-center justify-center text-slate-600 hover:border-[#5848FF] hover:text-[#5848FF] transition-colors"><ImagePlus className="w-6 h-6 mb-1" /><span className="text-xs">{t('newRequest.uploadPhoto')}</span></button>
                    <button type="button" onClick={() => cameraInputRef.current?.click()} data-testid="take-photo" className="bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 p-4 flex flex-col items-center justify-center text-slate-600 hover:border-[#5848FF] hover:text-[#5848FF] transition-colors"><Camera className="w-6 h-6 mb-1" /><span className="text-xs">{t('newRequest.takePhoto')}</span></button>
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" data-testid="file-input" />
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} className="hidden" data-testid="camera-input" />
              </div>

              {/* Address */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('newRequest.address')} <span className="text-rose-500">*</span></h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 z-10" />
                    <Input data-testid="request-street" placeholder={t('newRequest.street')} value={street} onChange={e=>setStreet(e.target.value)} className="h-11 rounded-xl pl-9" />
                  </div>
                  <Input data-testid="request-number" placeholder={t('newRequest.number')} value={number} onChange={e=>setNumber(e.target.value)} className="h-11 rounded-xl" />
                  <Select value={city} onValueChange={setCity}>
                    <SelectTrigger data-testid="request-district" className="h-11 rounded-xl"><SelectValue placeholder={t('newRequest.selectDistrict')} /></SelectTrigger>
                    <SelectContent className="bg-white rounded-xl max-h-60">{DISTRETTI_TICINO.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input data-testid="request-cap" placeholder={t('newRequest.cap')} value={cap} onChange={e=>handleCapChange(e.target.value)} maxLength={4} className="h-11 rounded-xl" />
                </div>
              </div>

              {/* Days & Slots */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('newRequest.preferredDays')} <span className="text-rose-500">*</span></h2>
                <div className="flex flex-wrap gap-2 mb-6">
                  {DAYS.map((d, idx)=> <button type="button" key={idx} onClick={()=>toggleDay(d)} data-testid={`day-${d}`} className={`px-4 h-10 rounded-xl text-sm border-2 font-medium transition-colors ${days.includes(d)? 'bg-[#5848FF] text-white border-[#5848FF]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#5848FF]'}`}>{d}</button>)}
                </div>
                <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('newRequest.preferredSlots')} <span className="text-rose-500">*</span></h2>
                <div className="flex flex-wrap gap-2">
                  {SLOTS.map(s=> <button type="button" key={s.key} onClick={()=>toggleSlot(s.key)} data-testid={`slot-${s.key}`} className={`px-4 h-10 rounded-xl text-sm border-2 font-medium transition-colors ${slots.includes(s.key)? 'bg-[#5848FF] text-white border-[#5848FF]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#5848FF]'}`}>{s.label}</button>)}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 space-y-6">
                {/* Contact Preference */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                  <h2 className="text-lg font-semibold text-slate-900 mb-3">{t('ui.howToBeContacted')} <span className="text-rose-500">*</span></h2>
                  <div className="space-y-2 mb-4">
                    <button type="button" onClick={() => setContactPreference('whatsapp')} className={`w-full px-4 h-11 rounded-xl text-sm border-2 font-medium flex items-center gap-3 transition-colors ${contactPreference === 'whatsapp' ? 'bg-green-500 text-white border-green-500' : 'bg-white text-slate-600 border-slate-200 hover:border-green-300'}`}>
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      WhatsApp
                    </button>
                    <button type="button" onClick={() => setContactPreference('telefono')} className={`w-full px-4 h-11 rounded-xl text-sm border-2 font-medium flex items-center gap-3 transition-colors ${contactPreference === 'telefono' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                      Telefono
                    </button>
                    <button type="button" onClick={() => setContactPreference('email')} className={`w-full px-4 h-11 rounded-xl text-sm border-2 font-medium flex items-center gap-3 transition-colors ${contactPreference === 'email' ? 'bg-purple-500 text-white border-purple-500' : 'bg-white text-slate-600 border-slate-200 hover:border-purple-300'}`}>
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                      Email
                    </button>
                  </div>
                  <Input data-testid="contact-value" type={contactPreference === 'email' ? 'email' : 'tel'} placeholder={contactPreference === 'email' ? t('ui.yourEmail') : t('ui.yourNumber')} value={contactValue} onChange={e => setContactValue(e.target.value)} className="h-11 rounded-xl bg-white" />
                  <p className="text-xs text-slate-500 mt-2">Le ditte interessate ti contatteranno direttamente su questo recapito</p>
                </div>

                {/* Submit */}
                <Button data-testid="request-submit" type="submit" className="w-full h-14 bg-[#5848FF] hover:bg-[#4638EF] text-white text-base font-semibold rounded-xl shadow-lg shadow-indigo-200">
                  <Send className="w-5 h-5 mr-2" />{t('newRequest.submitRequest')}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </main>

      <Dialog open={successOpen} onOpenChange={() => { setSuccessOpen(false); nav('/cliente/dashboard'); }}>
        <DialogContent className="rounded-2xl bg-white max-w-sm" data-testid="success-dialog">
          <div className="flex flex-col items-center justify-center py-6 px-4 text-center">
            <div className="mb-4 flex items-center justify-center h-16 w-16 rounded-full bg-emerald-100"><CheckCircle2 className="w-10 h-10 text-emerald-500" /></div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('newRequest.excellent')}</h2>
            <p className="text-slate-600 mb-2">{t('ui.requestPublished')}</p>
            <p className="text-sm text-slate-500 mb-6">{t('ui.willBeContacted')}</p>
            <Button onClick={() => { setSuccessOpen(false); nav('/cliente/dashboard'); }} className="w-full bg-[#5848FF] hover:bg-[#4638EF] text-white h-11 rounded-xl" data-testid="success-ok-button">{t('common.ok')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
